import { memo, useCallback, useMemo, useRef } from "react";
import useAthleteStore from "providers/AthletesProvider/useAthleteStore";
import Tables from "components/Tables";
import Typography from "@mui/material/Typography";
import useSearchText from "providers/SearchTextProvider/useSearchText";
import NoDataWrapper from "components/NoDataWrapper";
import useAthletesFilter from "../hooks/useAthletesFilter";
import { useVirtualizer } from "@tanstack/react-virtual";
import AthleteTableRow from "./AthleteTableRow";
import VirtualTable from "components/VirtualTable";
import { Trans, useTranslation } from "react-i18next";

function AthleteTableRowContainer() {
  const athletes = useAthleteStore();
  const searchText = useSearchText();
  const parentRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const noResultsMessage = useMemo(() => {
    if (searchText) {
      return (
        <Trans i18nKey="global.no_search_results" values={{ searchText }}>
          There are no results for your search term
          <Typography component="span" variant="h4" sx={{ fontWeight: "bold" }}>
            <>{{ searchText }}...</>
          </Typography>
        </Trans>
      );
    }
    return t("athlete.table_no_results_text");
  }, [searchText, t]);

  const filteredAthletes = useAthletesFilter(athletes);

  const rowVirtualizer = useVirtualizer({
    count: filteredAthletes.length,
    getScrollElement: () => parentRef.current,
    estimateSize: useCallback(() => 57, []),
    overscan: 15,
  });

  return (
    <Tables.Components.RowsBox>
      <NoDataWrapper
        hasData={Boolean(filteredAthletes.length)}
        fallback={<Tables.Components.Skeleton text={noResultsMessage} />}
      >
        <VirtualTable.Parent ref={parentRef}>
          <VirtualTable.Inner sx={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
            {rowVirtualizer.getVirtualItems().map((virtualRow) => (
              <VirtualTable.Row
                key={virtualRow.index}
                data-index={virtualRow.index}
                ref={rowVirtualizer.measureElement}
                style={{ height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)` }}
              >
                <AthleteTableRow athlete={filteredAthletes[virtualRow.index]} />
              </VirtualTable.Row>
            ))}
          </VirtualTable.Inner>
        </VirtualTable.Parent>
      </NoDataWrapper>
    </Tables.Components.RowsBox>
  );
}

export default memo(AthleteTableRowContainer);
